import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getNotificaciones, marcarTodasLeidas, marcarLeida } from "../../features/portal/services/portalService";

// Sondeo cada 4s — sin websockets, esto es lo más rápido que se puede llegar
// sin cambiar la infraestructura de despliegue, y ya se siente casi inmediato.
const POLL_INTERVAL = 4000;

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifs,      setNotifs]      = useState([]);
  const [unread,      setUnread]      = useState(0);
  const [justArrived, setJustArrived] = useState([]);
  const seenIds = useRef(null); // null = todavía no hicimos el primer sondeo

  const poll = useCallback(async () => {
    try {
      const data = await getNotificaciones();
      const list = data.results || [];
      setNotifs(list);
      setUnread(data.unread || 0);

      if (seenIds.current !== null) {
        const nuevas = list.filter(n => !seenIds.current.has(n.id));
        if (nuevas.length > 0) {
          setJustArrived(prev => [...prev, ...nuevas]);
          // Si hay alguien viendo la página relacionada, que se refresque sola
          // en vez de esperar a que la persona la recargue a mano.
          nuevas.forEach(n => {
            window.dispatchEvent(new CustomEvent("portal:data-changed", { detail: { tipo: n.tipo, link: n.link } }));
          });
        }
      }
      seenIds.current = new Set(list.map(n => n.id));
    } catch { /* silencioso — no queremos que un poll fallido rompa la pantalla */ }
  }, []);

  useEffect(() => {
    poll();
    const timer = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [poll]);

  const dismissArrived = useCallback((id) => {
    setJustArrived(prev => prev.filter(n => n.id !== id));
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await marcarTodasLeidas();
      setUnread(0);
      setNotifs(prev => prev.map(n => ({ ...n, leida: true })));
    } catch { /* silencioso */ }
  }, []);

  const markOneRead = useCallback(async (id) => {
    try {
      await marcarLeida(id);
      setNotifs(prev => prev.map(n => (n.id === id ? { ...n, leida: true } : n)));
    } catch { /* silencioso */ }
  }, []);

  return (
    <NotificationContext.Provider value={{ notifs, unread, justArrived, dismissArrived, markAllRead, markOneRead, refetch: poll }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications debe usarse dentro de <NotificationProvider>");
  return ctx;
}
